import { memo, useMemo } from 'react';
import Svg, { Circle, G, Line, Polygon, Text } from 'react-native-svg';
import { ComboTheme } from '../ComboChart/types';
import { RadarSeries, kRadarRings, radarLayout, radarPoint } from './layout';

export interface RadarSeriesChartProps {
  width: number;
  height: number;
  /** The spokes, clockwise from the top. */
  spokes: string[];
  /** One polygon each. */
  series: RadarSeries[];
  /** Each series against its own maximum, not one shared scale. */
  perSeries: boolean;
  /** Each polygon's fill, 0..1. */
  fillOpacity: number;
  dataLabels: boolean;
  /** A ring's value as text, on a shared scale. */
  format: (value: number) => string;
  theme: ComboTheme;
  /** A shared scale reaches at least this, e.g. 100 for scores. */
  scaleFloor?: number;
}

/**
 * A radar (spider) chart: a web of spokes, one polygon per series. On a
 * shared scale the rings carry their values up the top spoke.
 */
export const RadarSeriesChart = memo((props: RadarSeriesChartProps) => {
  const { width, height, spokes, series, theme } = props;
  const f = theme.fontSize;
  const layout = useMemo(
    () =>
      radarLayout({
        width,
        height,
        spokes,
        series,
        perSeries: props.perSeries,
        fontSize: f,
        scaleFloor: props.scaleFloor,
      }),
    [width, height, spokes, series, props.perSeries, f, props.scaleFloor],
  );
  const { radius, angles } = layout;
  const { sharedMax } = layout;
  const ringShares = sharedMax
    ? layout.rings.map(ring => ring / sharedMax)
    : Array.from({ length: kRadarRings }, (_, i) => (i + 1) / kRadarRings);
  const web = (share: number) =>
    angles
      .map(angle => {
        const p = radarPoint(layout, angle, radius * share);
        return `${p.x},${p.y}`;
      })
      .join(' ');
  const pointsOf = (entry: RadarSeries, s: number) =>
    angles.map((angle, i) => {
      const value = entry.values[i];
      const share =
        value === undefined || !Number.isFinite(value)
          ? 0
          : Math.min(Math.max(value, 0) / layout.maxOf[s], 1);
      return radarPoint(layout, angle, radius * share);
    });
  return (
    <Svg width={width} height={height}>
      {ringShares.map(share => (
        <Polygon
          key={share}
          points={web(share)}
          fill="none"
          stroke={theme.grid}
          strokeWidth={1}
        />
      ))}
      {angles.map((angle, i) => {
        const end = radarPoint(layout, angle, radius);
        const at = radarPoint(layout, angle, radius + 6);
        const sin = Math.sin(angle);
        const cos = Math.cos(angle);
        return (
          <G key={i}>
            <Line
              x1={layout.cx}
              y1={layout.cy}
              x2={end.x}
              y2={end.y}
              stroke={theme.axisLine}
              strokeWidth={1}
            />
            <Text
              x={at.x}
              y={at.y + (cos > 0.3 ? 0 : cos < -0.3 ? f * 0.9 : f * 0.35)}
              fontSize={f}
              fontFamily={theme.fontFamily}
              fill={theme.axisText}
              textAnchor={sin > 0.3 ? 'start' : sin < -0.3 ? 'end' : 'middle'}
            >
              {layout.names[i]}
            </Text>
          </G>
        );
      })}
      {(sharedMax ? layout.rings : []).map(ring => (
        <Text
          key={`r-${ring}`}
          x={layout.cx + 3}
          // Just inside the ring, clear of the top spoke's name above it.
          y={layout.cy - (radius * ring) / (sharedMax ?? 1) + f}
          fontSize={f * 0.9}
          fontFamily={theme.fontFamily}
          fill={theme.axisText}
        >
          {props.format(ring)}
        </Text>
      ))}
      {series.map((entry, s) => {
        const points = pointsOf(entry, s);
        return (
          <G key={entry.key}>
            <Polygon
              points={points.map(p => `${p.x},${p.y}`).join(' ')}
              fill={entry.color}
              fillOpacity={props.fillOpacity}
              stroke={entry.color}
              strokeWidth={2}
              strokeLinejoin="round"
            />
            {points.map((p, i) =>
              entry.values[i] === undefined ? null : (
                <Circle
                  key={i}
                  cx={p.x}
                  cy={p.y}
                  r={2.5}
                  fill={theme.background}
                  stroke={entry.color}
                  strokeWidth={1.5}
                />
              ),
            )}
          </G>
        );
      })}
      {props.dataLabels
        ? series.flatMap((entry, s) =>
            pointsOf(entry, s).map((p, i) => {
              const text = entry.labels?.[i];
              if (!text || entry.values[i] === undefined) return null;
              // Every other series labels inward, so two close polygons'
              // labels do not print over each other.
              const out = radarPoint(
                { cx: p.x, cy: p.y },
                angles[i],
                s % 2 === 0 ? f * 0.9 : -f * 0.9,
              );
              return (
                <Text
                  key={`l-${entry.key}-${i}`}
                  x={out.x}
                  y={out.y + f * 0.35}
                  fontSize={f}
                  fontFamily={theme.fontFamily}
                  fill={entry.color}
                  textAnchor="middle"
                >
                  {text}
                </Text>
              );
            }),
          )
        : null}
    </Svg>
  );
});
